/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { TextNode } from 'lexical';
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { useLazyQuery } from '@apollo/client';
import _ from 'lodash';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from '../../../../../../../apps/web2/app/components/ui/popover';
import { useTheme } from '@chakra-ui/react';
import { $createMentionNode } from '../nodes/MentionNode';
import { gql } from '../../../../../__generated__/gql';
import { MentionUserFragment } from '../../../../../__generated__/graphql';
import { useThemeColors } from '../../../../hooks/useThemeColors';
import PerfImageFromFile from 'shared/misc/components/util/PerfImageFromFile';

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']';

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ['@'].join('');

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]';

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')';

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$',
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$',
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5;

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      };
    }
  }
  return null;
}

gql(/* GraphQL */ `
  fragment MentionsSearch on Query {
    userConnection(
      first: $first
      after: $after
      search: $search
      includeGuests: true
      includeUnfinishedSignUp: false
      includePerfectlyImperfect: true
      includeSelf: false
    ) @connection(key: "mentionsPlugin") {
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          ...UserCore
        }
      }
    }
  }

  fragment MentionUser on User {
    id
    username
    firstName
    lastName
    createdAt
    updatedAt
    avatarPhotoSrc
  }
`);

const MentionsSearchQuery = gql(/* GraphQL */ `
  query getMentionsSearch($first: Int, $after: String, $search: String) {
    ...MentionsSearch
  }
`);

const PAGE_LENGTH = 10;

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignMentions(text, 0);
}

class MentionTypeaheadOption extends MenuOption {
  user: MentionUserFragment;
  constructor(user: MentionUserFragment) {
    super(user.id);
    this.user = user;
  }
}

function MentionsTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: MentionTypeaheadOption;
}) {
  const themeColors = useThemeColors();

  return (
    <div
      className={`flex bg-brand-background cursor-pointer items-center ${
        !isSelected ? 'text-brand-main' : 'text-brand-highlight'
      }`}
      key={option.key}
      tabIndex={-1}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <PerfImageFromFile
        fileKey={
          option.user?.avatarPhotoSrc ||
          `/default-profile-photo-${
            themeColors.main === '#ffffff' || themeColors.main === 'white' ? 'white' : 'blue'
          }-small.png`
        }
        useFallbackAvatarSrc
        width={24}
        height={24}
        alt="image"
        quality={20}
        style={{
          minWidth: '16px',
          minHeight: '16px',
        }}
      />
      <span className="typography-action-sm ml-1.5">@{option.user.username}</span>
    </div>
  );
}

export default function NewMentionsPlugin({
  isReadOnly,
}: {
  isReadOnly: boolean;
}): JSX.Element | null {
  const [queryString, setQueryString] = useState<string | null>(null);

  const [loadMentions, { variables, previousData, data = previousData, refetch }] = useLazyQuery(
    MentionsSearchQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        first: PAGE_LENGTH,
      },
    },
  );

  const debouncedRefetch = useMemo(() => {
    return _.debounce((nextVariables) => {
      if (!isReadOnly) refetch(nextVariables);
    }, 750);
  }, [refetch, isReadOnly]);

  useEffect(() => {
    if (variables && variables?.search !== queryString && queryString && !isReadOnly) {
      debouncedRefetch({
        search: queryString,
        first: PAGE_LENGTH,
        after: null,
      });
    }
  }, [queryString, isReadOnly]);

  const [editor] = useLexicalComposerContext();

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
  });

  const options = useMemo(
    () => (data?.userConnection?.edges || []).map(({ node }) => new MentionTypeaheadOption(node)),
    [data?.userConnection],
  );

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(selectedOption.key, selectedOption.user);
        if (nodeToReplace) {
          // nodeToReplace.replace(mentionNode);
          const parent = nodeToReplace.getParentOrThrow();
          console.log({ parent });
          // parent.append(mentionNode);
          nodeToReplace.insertAfter(mentionNode);
          nodeToReplace.remove();
        }
        mentionNode.selectNext();
        closeMenu();
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor);
      if (slashMatch !== null) {
        return null;
      }
      return getPossibleQueryMatch(text);
    },
    [checkForSlashTriggerMatch, editor],
  );

  const MemoizedMenuRenderFn = useMemo(() => {
    return (props) => {
      const { anchorElementRef, selectedIndex, selectOptionAndCleanUp, setHighlightedIndex } =
        props;
      return anchorElementRef.current && options.length > 0
        ? ReactDOM.createPortal(
            <Popover open modal={false}>
              <PopoverAnchor asChild>
                <div className="mt-[12px] mt-[-60px]" />
              </PopoverAnchor>
              <PopoverContent
                className="min-w-[200px] max-h-[150px] bg-brand-background border border-brand-border overflow-y-auto md:max-w-[300px] z-[2147483649] p-[6px]"
                align="start"
                side="bottom"
                sideOffset={5}
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <div className="flex flex-col gap-[6px]">
                  {options.map((option, i: number) => (
                    <MentionsTypeaheadMenuItem
                      index={i}
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i);
                      }}
                      key={option.key}
                      option={option}
                    />
                  ))}
                </div>
              </PopoverContent>
            </Popover>,
            anchorElementRef.current,
          )
        : null;
    };
  }, [options]);

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onOpen={() => loadMentions({ variables: { search: queryString } })}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) => {
        return (
          <MemoizedMenuRenderFn
            anchorElementRef={anchorElementRef}
            selectedIndex={selectedIndex}
            selectOptionAndCleanUp={selectOptionAndCleanUp}
            setHighlightedIndex={setHighlightedIndex}
          />
        );
      }}
    />
  );
}
